/**
 * @fileoverview Main Application Component - Hi-Tech Textile HR & Payroll System
 * @description Root component with routing and providers.
 */

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ViewModeProvider } from "@/contexts/ViewModeContext";
import { WindowManagerProvider } from "@/contexts/WindowManagerContext";
import { SessionTimeoutProvider } from "@/components/auth/SessionTimeoutProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PerfDashboard } from "@/components/dev/PerfDashboard";
import { RouteWindowHost } from "@/components/layout/RouteWindowHost";
import { MinimizedTaskbar } from "@/components/layout/MinimizedTaskbar";
import { AppRoutes } from "@/AppRoutes";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <ErrorBoundary fallbackMessage="The application encountered an error">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <ViewModeProvider>
            <WindowManagerProvider>
              <SessionTimeoutProvider>
                <Toaster />
                <Sonner />
                <PerfDashboard />
                <BrowserRouter>
                  <RouteWindowHost render={(loc) => <AppRoutes location={loc} />} />
                  <MinimizedTaskbar />
                </BrowserRouter>
              </SessionTimeoutProvider>
            </WindowManagerProvider>
          </ViewModeProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
